import React from 'react';
import classNames from 'classnames';
import { IconButton } from '@mui/material';
import styles from './table-v2.module.scss';
import { TiminatorTableColumn } from './types';
import { SortConfig } from './hooks/useTableSorting';
import { SVGS } from '../../../assets/images';

interface TableHeaderProps<Entity> {
    columns: TiminatorTableColumn<Entity>[];
    gridTemplateColumns: string;
    sortConfig: SortConfig | null;
    onSortClick: (column: TiminatorTableColumn<Entity>) => void;
}

const TableHeaderComponent = <Entity,>({
    columns,
    gridTemplateColumns,
    sortConfig,
    onSortClick,
}: TableHeaderProps<Entity>) => {
    const getSortIconClassName = React.useCallback((columnId: string) => {
        if (!sortConfig || sortConfig.columnId !== columnId) {
            return styles.sortIcon;
        }

        return classNames(styles.sortIcon, styles.sortIconActive, {
            [styles.sortIconAsc]: sortConfig.direction === 'asc',
            [styles.sortIconDesc]: sortConfig.direction === 'desc',
        });
    }, [sortConfig]);

    return (
        <div className={styles.tableHeaderWrapper}>
            <div
                className={styles.tableHeaderRow}
                style={{ display: 'grid', gridTemplateColumns }}>
                {columns.map((column) => (
                    <div
                        key={`header-${column.id}`}
                        className={styles.tableHeaderCell}
                        style={{
                            textAlign:
                                column.align === 'right'
                                    ? 'right'
                                    : column.align === 'center'
                                        ? 'center'
                                        : 'left',
                        }}>
                        <div className={styles.tableHeaderCellContent}>
                            <span className={styles.tableHeaderLabel}>{column.label ?? ''}</span>
                            {column.sortable ? (
                                <IconButton
                                    aria-label={`sort-${column.id}`}
                                    className={styles.sortButton}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onSortClick(column);
                                    }}
                                    size='small'
                                    disableRipple>
                                    <SVGS.KeyboardArrowDownIcon className={getSortIconClassName(column.id)} />
                                </IconButton>
                            ) : null}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TableHeader = React.memo(TableHeaderComponent) as typeof TableHeaderComponent;

export default TableHeader;
